import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  CalendarDays, 
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  Paperclip,
  Eye,
  User,
  FileText,
  Check,
  X,
  RefreshCw
} from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatUtcDate } from '@/utils/dateUtils';

function ManagerLeaveManagementPage() {
  const { t } = useLanguage();
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionType, setActionType] = useState('');
  const [adminRemarks, setAdminRemarks] = useState('');
  const [isPaid, setIsPaid] = useState(null); // true | false | null
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });

  useEffect(() => {
    fetchLeaveRequests();
  }, [activeTab]);

  const fetchLeaveRequests = async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeTab !== 'all') {
        params.status = activeTab;
      }
      
      const response = await axios.get(`${API}/manager/leave-requests`, {
        params,
        withCredentials: true
      });
      setLeaveRequests(response.data.leave_requests || []);
      setCounts(response.data.counts || { pending: 0, approved: 0, rejected: 0 });
    } catch (error) {
      console.error('Failed to fetch leave requests:', error);
      showMessage('error', error.response?.data?.detail || 'Failed to load team leave requests');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleAction = async () => {
    if (!selectedRequest || !actionType) return;
    if (actionType === 'approve' && isPaid === null) {
      showMessage('error', 'Please choose paid or unpaid salary for this leave');
      return;
    }
    
    setProcessing(true);
    try {
      const payload = {
        action: actionType,
        admin_remarks: adminRemarks || null,
      };
      if (actionType === 'approve') {
        payload.is_paid = isPaid;
      }
      await axios.put(
        `${API}/manager/leave-request/${selectedRequest.leave_id}`,
        payload,
        { withCredentials: true }
      );
      
      const paidLabel = actionType === 'approve'
        ? (isPaid ? ' (paid salary)' : ' (unpaid salary)')
        : '';
      showMessage('success', `Leave request ${actionType}d successfully${paidLabel}`);
      setShowActionDialog(false);
      setShowDetailDialog(false);
      setAdminRemarks('');
      setIsPaid(null);
      fetchLeaveRequests();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || `Failed to ${actionType} request`);
    } finally {
      setProcessing(false);
    }
  };

  const openActionDialog = (request, action) => {
    setSelectedRequest(request);
    setActionType(action);
    setAdminRemarks('');
    // Default: off_day / unpaid types → unpaid; others → paid (admin can change)
    if (action === 'approve') {
      const unpaidTypes = ['off_day', 'unpaid'];
      setIsPaid(unpaidTypes.includes(request.leave_type) ? false : true);
    } else {
      setIsPaid(null);
    }
    setShowActionDialog(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
      case 'approved':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300"><CheckCircle className="w-3 h-3 mr-1" /> Approved</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getLeaveTypeBadge = (type) => {
    const types = {
      'off_day': { label: 'Off Day', color: 'bg-blue-50 text-blue-700' },
      'sick_leave': { label: 'Sick Leave', color: 'bg-red-50 text-red-700' },
      'vacation': { label: 'Vacation', color: 'bg-purple-50 text-purple-700' },
      'personal': { label: 'Personal', color: 'bg-gray-50 text-gray-700' },
      'emergency': { label: 'Emergency', color: 'bg-orange-50 text-orange-700' }
    };
    const config = types[type] || { label: type, color: 'bg-gray-50 text-gray-700' };
    return <Badge variant="outline" className={config.color}>{config.label}</Badge>;
  };

  const getSalaryBadge = (request) => {
    if (request.status !== 'approved' || request.is_paid == null) return null;
    return request.is_paid ? (
      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Paid</Badge>
    ) : (
      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Unpaid</Badge>
    );
  };

  const formatDate = (dateStr) => {
    return formatUtcDate(dateStr + 'T00:00:00');
  };

  const renderTable = (requests) => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Staff</TableHead>
          <TableHead>Subject</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Date Range</TableHead>
          <TableHead>Days</TableHead>
          <TableHead>{t("common.status")}</TableHead>
          <TableHead>{t("common.actions")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {requests.map((request) => (
          <TableRow key={request.leave_id}>
            <TableCell>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-sm">{request.staff_name}</p>
                  <p className="text-xs text-gray-500">{request.staff_department || 'No Dept'}</p>
                </div>
              </div>
            </TableCell>
            <TableCell className="font-medium max-w-[200px] truncate">{request.subject}</TableCell>
            <TableCell>
              <div className="flex flex-col gap-1">
                {getLeaveTypeBadge(request.leave_type)}
                {getSalaryBadge(request)}
              </div>
            </TableCell>
            <TableCell>
              <span className="text-sm">
                {formatDate(request.start_date)} - {formatDate(request.end_date)}
              </span>
            </TableCell>
            <TableCell className="text-center">{request.days_count}</TableCell>
            <TableCell>{getStatusBadge(request.status)}</TableCell>
            <TableCell>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedRequest(request);
                    setShowDetailDialog(true);
                  }}
                  data-testid={`view-leave-${request.leave_id}`}
                >
                  <Eye className="w-4 h-4" />
                </Button>
                {request.status === 'pending' && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-green-600 hover:text-green-700 hover:bg-green-50"
                      onClick={() => openActionDialog(request, 'approve')}
                      data-testid={`approve-leave-${request.leave_id}`}
                    >
                      <Check className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => openActionDialog(request, 'reject')}
                      data-testid={`reject-leave-${request.leave_id}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <div className="space-y-6">
      {/* Message */}
      {message.text && (
        <div className={`p-4 rounded-lg ${
          message.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' :
          message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
          'bg-blue-50 text-blue-700 border border-blue-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team Leave Requests</h1>
          <p className="text-gray-600">Approve or reject leave for your team — paid or unpaid</p>
        </div>
        <Button 
          variant="outline"
          onClick={fetchLeaveRequests}
          data-testid="refresh-leave-requests-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-yellow-600 text-sm font-medium">{t("common.pending")}</p>
                <p className="text-2xl font-bold text-yellow-700">{counts.pending}</p>
              </div>
              <Clock className="w-10 h-10 text-yellow-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-green-600 text-sm font-medium">{t("leave.approved")}</p>
                <p className="text-2xl font-bold text-green-700">{counts.approved}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-red-600 text-sm font-medium">{t("leave.rejected")}</p>
                <p className="text-2xl font-bold text-red-700">{counts.rejected}</p>
              </div>
              <XCircle className="w-10 h-10 text-red-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Leave Requests */}
      <Card>
        <CardHeader className="border-b">
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-blue-600" />
                Team Leave Requests
              </CardTitle>
              <CardDescription>Only staff on your managed teams</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="border-b px-4">
              <TabsList className="bg-transparent">
                <TabsTrigger value="pending" className="data-[state=active]:bg-yellow-50 data-[state=active]:text-yellow-700">
                  Pending ({counts.pending})
                </TabsTrigger>
                <TabsTrigger value="approved" className="data-[state=active]:bg-green-50 data-[state=active]:text-green-700">
                  Approved ({counts.approved})
                </TabsTrigger>
                <TabsTrigger value="rejected" className="data-[state=active]:bg-red-50 data-[state=active]:text-red-700">
                  Rejected ({counts.rejected})
                </TabsTrigger>
                <TabsTrigger value="all">All</TabsTrigger>
              </TabsList>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : leaveRequests.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <CalendarDays className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No leave requests found</p>
              </div>
            ) : (
              <div className="p-4">
                {renderTable(leaveRequests)}
              </div>
            )}
          </Tabs>
        </CardContent>
      </Card>

      {/* Request Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Leave Request Details
            </DialogTitle>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold">{selectedRequest.staff_name}</p>
                  <p className="text-sm text-gray-500">
                    {selectedRequest.staff_email} • {selectedRequest.staff_department || 'No Dept'}
                  </p>
                </div>
              </div>

              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold text-lg">{selectedRequest.subject}</h3>
                  <div className="flex gap-2 mt-1 flex-wrap">
                    {getLeaveTypeBadge(selectedRequest.leave_type)}
                    {getStatusBadge(selectedRequest.status)}
                    {getSalaryBadge(selectedRequest)}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg">
                <div>
                  <p className="text-xs text-gray-500">Start Date</p>
                  <p className="font-medium">{formatDate(selectedRequest.start_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">End Date</p>
                  <p className="font-medium">{formatDate(selectedRequest.end_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Total Days</p>
                  <p className="font-medium">{selectedRequest.days_count} day(s)</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Submitted</p>
                  <p className="font-medium">
                    {formatUtcDate(selectedRequest.created_at)}
                  </p>
                </div>
              </div>

              <div>
                <p className="text-xs text-gray-500 mb-1">Reason</p>
                <p className="text-gray-700 bg-white p-3 rounded border">{selectedRequest.reason}</p>
              </div>

              {selectedRequest.attachment_url && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Attachment</p>
                  <a
                    href={selectedRequest.attachment_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
                  >
                    <Paperclip className="w-4 h-4" />
                    View Attachment
                  </a>
                </div>
              )}

              {selectedRequest.admin_remarks && (
                <div className={`p-4 rounded-lg ${
                  selectedRequest.status === 'approved' ? 'bg-green-50 border border-green-200' :
                  selectedRequest.status === 'rejected' ? 'bg-red-50 border border-red-200' :
                  'bg-gray-50'
                }`}>
                  <p className="text-xs text-gray-500 mb-1">Admin Remarks</p>
                  <p className="font-medium">{selectedRequest.admin_remarks}</p>
                  {selectedRequest.reviewed_at && (
                    <p className="text-xs text-gray-500 mt-2">
                      Reviewed on {formatUtcDate(selectedRequest.reviewed_at)}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {selectedRequest?.status === 'pending' && (
              <>
                <Button
                  variant="outline"
                  className="text-red-600 border-red-300 hover:bg-red-50"
                  onClick={() => openActionDialog(selectedRequest, 'reject')}
                >
                  <X className="w-4 h-4 mr-2" />
                  Reject
                </Button>
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => openActionDialog(selectedRequest, 'approve')}
                >
                  <Check className="w-4 h-4 mr-2" />
                  Approve
                </Button>
              </>
            )}
            {selectedRequest?.status !== 'pending' && (
              <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Action Confirmation Dialog */}
      <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${
              actionType === 'approve' ? 'text-green-700' : 'text-red-700'
            }`}>
              {actionType === 'approve' ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <XCircle className="w-5 h-5" />
              )}
              {actionType === 'approve' ? 'Approve' : 'Reject'} Leave Request
            </DialogTitle>
            <DialogDescription>
              {actionType === 'approve' 
                ? 'Choose whether this leave should count toward paid salary, then confirm.'
                : 'This will reject the leave request. Please provide a reason.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {selectedRequest && (
              <div className="p-3 bg-gray-50 rounded-lg text-sm">
                <p><strong>{selectedRequest.staff_name}</strong></p>
                <p className="text-gray-600">{selectedRequest.subject}</p>
                <p className="text-gray-500 mt-1">
                  {formatDate(selectedRequest.start_date)} - {formatDate(selectedRequest.end_date)} ({selectedRequest.days_count} days)
                </p>
              </div>
            )}

            {actionType === 'approve' && (
              <div className="space-y-2">
                <Label>Salary treatment *</Label>
                <div className="grid grid-cols-1 gap-2">
                  <button
                    type="button"
                    onClick={() => setIsPaid(true)}
                    data-testid="approve-paid-option"
                    className={`text-left rounded-lg border p-3 transition-colors ${
                      isPaid === true
                        ? 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-500'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <p className="text-sm font-semibold text-emerald-800">Approve with paid salary</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Leave days count as paid — included in base pay / no unpaid deduction.
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsPaid(false)}
                    data-testid="approve-unpaid-option"
                    className={`text-left rounded-lg border p-3 transition-colors ${
                      isPaid === false
                        ? 'border-amber-500 bg-amber-50 ring-1 ring-amber-500'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <p className="text-sm font-semibold text-amber-800">Approve with unpaid salary</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Leave days are unpaid — excluded from base pay like an absence.
                    </p>
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="admin_remarks">
                Remarks {actionType === 'reject' ? '*' : '(Optional)'}
              </Label>
              <Textarea
                id="admin_remarks"
                placeholder={actionType === 'approve' 
                  ? 'e.g., Approved. Enjoy your time off!'
                  : 'e.g., Request denied due to project deadline...'
                }
                value={adminRemarks}
                onChange={(e) => setAdminRemarks(e.target.value)}
                rows={3}
                data-testid="admin-remarks-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActionDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAction}
              disabled={
                processing
                || (actionType === 'reject' && !adminRemarks)
                || (actionType === 'approve' && isPaid === null)
              }
              className={actionType === 'approve' 
                ? 'bg-green-600 hover:bg-green-700' 
                : 'bg-red-600 hover:bg-red-700'
              }
              data-testid="confirm-action-btn"
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  {actionType === 'approve' ? <Check className="w-4 h-4 mr-2" /> : <X className="w-4 h-4 mr-2" />}
                  {actionType === 'approve'
                    ? `Confirm ${isPaid === false ? 'Unpaid' : isPaid === true ? 'Paid' : ''} Approval`.replace(/\s+/g, ' ').trim()
                    : 'Confirm Rejection'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ManagerLeaveManagementPage;
